/*
 * Byte-identical in Cortex and Quantum.
 *
 * A CAMPAIGN'S OWN WORDS ARE NOT WORD-SWAPPED.
 *
 * The strategy page words Cphere's template copy for the campaign's category
 * (config/verticalIntelligenceConfig.js, sanitizeVerticalIntelligenceCopy):
 * on a non-film page other verticals' signature terms are neutralized
 * ("volunteers" → "participants", "travelers" → "audiences", "ticket sales" →
 * "qualified response"). The page applied that to everything it rendered
 * through renderAdvertiserNarrativeCopy, the campaign's own saved values
 * included, so campaign 55's saved audience "Tennessee Volunteers" read
 * "Tennessee participants".
 *
 * The boundary is provenance. A value the campaign itself saved (its name,
 * advertiser, objective, KPI, notes and targeting) that the transformation
 * would alter is kept verbatim wherever it appears; the text around it is
 * transformed exactly as before. The transformation and its rules are
 * unchanged, and a value it would leave alone is not touched, so a page none
 * of whose own values it alters (film; campaign 53) renders as before.
 */

// Where the payload carries the campaign's own saved inputs.
const CAMPAIGN_OWN_FIELDS = Object.freeze([
  "campaignName",
  "campaign_name",
  "advertiserName",
  "advertiser_name",
  "advertiser",
  "objective",
  "primaryKpi",
  "primary_kpi",
  "notes",
  "targeting",
]);

const normalizeText = (value) => String(value).replace(/\s+/g, " ").trim();
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The campaign's saved values, whole and in their parts (a list of teams, a
// KPI and its clauses).
const campaignOwnValues = (campaign) => {
  const values = new Set();
  const add = (value) => {
    const whole = normalizeText(value);
    if (!/[A-Za-z]/.test(whole)) return;
    values.add(whole);
    for (const part of whole.split(/\s*(?:[;\n]|,\s|\s[—–]\s|\.\s)\s*/)) {
      const clean = part.replace(/^(?:and|or)\s+/i, "").replace(/[.,;:]+$/, "").trim();
      if (clean.length >= 3 && /[A-Za-z]/.test(clean)) values.add(clean);
    }
  };
  const walk = (value) => {
    if (typeof value === "string") add(value);
    else if (Array.isArray(value)) value.forEach(walk);
    else if (value && typeof value === "object") Object.values(value).forEach(walk);
  };
  for (const key of CAMPAIGN_OWN_FIELDS) walk(campaign?.[key]);
  return [...values];
};

/*
 * The guard for one campaign and one transformation: a pattern for each of the
 * campaign's own values that the transformation would alter, longest first. A
 * value of several words is matched in any case (template copy may quote it in
 * lower case); a single word only as it was saved.
 */
export const campaignOwnWordGuard = (campaign, transform) =>
  campaignOwnValues(campaign)
    .filter((value) => transform(value) !== value)
    .sort((a, b) => b.length - a.length)
    .map((value) => new RegExp(escapeRegExp(value).replace(/\s+/g, "\\s+"), /\s/.test(value) ? "gi" : "g"));

// The text transformed, with the campaign's own values in it kept verbatim.
export const transformKeepingOwnWords = (text, guard, transform) => {
  if (!Array.isArray(guard) || !guard.length || typeof text !== "string" || !text) return transform(text);
  const kept = [];
  let masked = text;
  for (const pattern of guard) {
    masked = masked.replace(pattern, (match) => {
      kept.push(match);
      return `${String.fromCharCode(0xe100 + kept.length - 1)}`;
    });
  }
  if (!kept.length) return transform(text);
  return transform(masked).replace(/([-])/g, (_, code) => kept[code.charCodeAt(0) - 0xe100]);
};
